export interface OutputPresentation {
  summary: string;
  details: string;
  sources: string[];
}

const EMAIL_REFERENCE = /\bEmails?\s*#\s*\d+(?:\s*(?:,|and|&)\s*#?\s*\d+)*/gi;
const EMAIL_ATTRIBUTION = /\bEmail\s*#\s*\d+\s+(?:states|says|records|shows|notes)\s+(?:that\s+)?/gi;
const EMAIL_NUMBER = /#\s*(\d+)/g;
const AUDIT_PREFIX = /^(?:source|sources|evidence)\s*:\s*/i;
const ACTION_OR_CONSEQUENCE = /\b(?:must|should|need(?:s|ed)?|require(?:s|d)?|confirm|reconcile|review|notify|determine|obtain|cannot|can't|may not|risk|conflict|concern|unclear|unknown|incorrect|before|until)\b/i;

function normalize(value: string): string {
  return value.replace(/\s+/g, " ").trim();
}

export function extractEmailSources(...values: Array<string | null | undefined>): string[] {
  const numbers: string[] = [];
  for (const value of values) {
    for (const reference of value?.match(EMAIL_REFERENCE) || []) {
      for (const match of reference.matchAll(EMAIL_NUMBER)) {
        if (!numbers.includes(match[1])) numbers.push(match[1]);
      }
    }
  }
  return numbers.map((number) => `Email #${number}`);
}

/** Remove audit citations from prose because the component renders them once in
 * a dedicated source footer. The underlying stored output is never changed. */
export function withoutEmailReferences(value: string): string {
  return normalize(value
    .replace(EMAIL_ATTRIBUTION, "")
    .replace(EMAIL_REFERENCE, "")
    .replace(/\(\s*\)/g, "")
    .replace(/\s+([,.;:])/g, "$1")
    .replace(/([,:;])\s*([.!?])/g, "$2")
    .replace(AUDIT_PREFIX, ""));
}

function sentences(value: string): string[] {
  return normalize(value)
    .split(/(?<=[.!?])\s+(?=[A-Z0-9'“\"])/)
    .map((sentence) => sentence.trim())
    .filter(Boolean);
}

function firstUsefulClause(value: string): string {
  const parts = value.split(/\s+(?:—|–)\s+|;\s+/).map(normalize).filter(Boolean);
  if (parts.length === 1) return parts[0];
  const first = parts[0];
  const consequence = parts.slice(1).find((part) => ACTION_OR_CONSEQUENCE.test(part));
  return consequence ? `${first}; ${consequence}` : first;
}

function conciseSummary(value: string, includeConsequence: boolean): string {
  const cleaned = withoutEmailReferences(value);
  const parts = sentences(cleaned);
  if (!parts.length) return "Details available for review.";

  const first = firstUsefulClause(parts[0]);
  if (!includeConsequence) return first;

  const important = parts.filter((part) => ACTION_OR_CONSEQUENCE.test(part)).slice(0, 2).map(firstUsefulClause);
  return important.length ? [...new Set(important)].join(" ") : first;
}

function regexEscape(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function trackerDateLabels(value?: string): string[] {
  const match = value?.match(/^(\d{4})-(\d{2})(?:-(\d{2}))?$/);
  if (!match) return value ? [value] : [];
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3] || "1")));
  const month = new Intl.DateTimeFormat("en", { month: "long", timeZone: "UTC" }).format(date);
  return match[3]
    ? [value!, `${month} ${Number(match[3])}, ${match[1]}`, `${month} ${Number(match[3])} ${match[1]}`]
    : [value!, `${month} ${match[1]}`];
}

/** The tracker date already has a dedicated column. Remove only that same date
 * from summary prose; different dates remain visible, and details stay intact. */
export function withoutRepeatedTrackerDate(summary: string, date?: string): string {
  let result = summary;
  for (const label of trackerDateLabels(date).sort((a, b) => b.length - a.length)) {
    const token = regexEscape(label);
    result = result
      .replace(new RegExp(`\\s*\\([^)]*${token}[^)]*\\)`, "gi"), "")
      .replace(new RegExp(`^${token}(?!-\\d{2})\\s*[,—–:-]?\\s*`, "i"), "")
      .replace(new RegExp(`\\s+(?:(?:scheduled|target(?:ed)?)\\s+for|due(?:\\s+on)?|on|by|at|for|in)\\s+${token}(?!-\\d{2})`, "gi"), "")
      .replace(new RegExp(`\\s*${token}(?!-\\d{2})`, "gi"), "");
  }
  return normalize(result)
    .replace(/\s+([,.;:])/g, "$1")
    .replace(/([,:;])\s*([.!?])/g, "$2")
    .replace(/\s+([.!?])/g, "$1");
}

export function reviewTrayPresentation(title: string, detail: string, justification?: string | null): OutputPresentation {
  const details = withoutEmailReferences(detail);
  const titleWithoutSources = withoutEmailReferences(title);
  const detailSummary = conciseSummary(detail, true);
  const summary = detailSummary.toLocaleLowerCase() === titleWithoutSources.toLocaleLowerCase()
    ? titleWithoutSources
    : detailSummary;
  return {
    summary,
    details,
    sources: extractEmailSources(title, detail, justification),
  };
}

export function trackerPresentation(event: string, source?: string, date?: string): OutputPresentation {
  const details = withoutEmailReferences(event);
  const summary = conciseSummary(withoutRepeatedTrackerDate(details, date), false);
  return {
    summary: summary || "Tracked event",
    details,
    sources: extractEmailSources(event, source),
  };
}

export function formatOutputSources(sources: string[]): string {
  if (sources.length === 1) return sources[0];
  return sources.length ? `Emails ${sources.map((source) => source.replace("Email ", "")).join(", ")}` : "";
}
